/**
 * M7 S37: Mission 1's world content (plan S37; spec §4; D-427 placeholders).
 *
 * - **The Column Nine field, in queue order** (`columnNineField`): along a seeded axis through the S30 site, front to
 *   back: the Tethys-yellow haulers (`prop.column_nine_hauler`, look `player_lease`), the gap, then the settler hulls
 *   (`prop.column_nine_settler`); only the last few settlers show the cream lease band (look `player_lease`, the
 *   settler's accent band), the rest are neutral. The order is the signature beat: the player sees who was let
 *   through first without anyone saying it (spec §17 "never say the conclusion"). Positions are float64 world
 *   metres in the site's system (the renderer rebases them, D-013).
 * - **The recorder** (`recorderPickup`): securing the lead hauler (queue 0) reports the `column_nine_recorder` pickup.
 * - **The Asset Recovery crew** (`m1AcceptCommands`): `world12.json m1.acceptCommands`'s `spawnStoryShip` (key
 *   `m1:assetRecovery`, system `site:column-nine`), issued once when the mission goes active (S37 part 2: at accept,
 *   not at the scan). The story service resolves the site to its system (`resolveStoryCommand`); the worker keys it,
 *   so a re-apply never doubles it. Killing them is the Charter crime detector's ordinary crime against Tethys if witnessed.
 * - **The lasting trace** (`columnNineTrace`): kept → a memorial (beacons and Holdfast visitors); sold → the field is
 *   stripped over `stripDays` online days. The trace's start day lives in `career.story.traces` (`recordM1Trace`), so
 *   it survives save and load.
 *
 * Event-driven (a site query, a mission step), never per frame; it may allocate. Tuning in `story/world12.json`.
 */
import { z } from 'zod'
import type { Career } from '../../career/types'
import world12Json from '../../data/story/world12.json'
import type { Vec3T } from '../../galaxy/layout/types'
import { FACTION_IDS, type FactionId } from '../../render/contracts'
import { missionOf } from '../../story/missions'
import { storyCommandSchema } from '../../story/schemas'
import { hasFlag, storyOf, withStory } from '../../story/state'
import type { StoryCommand, StoryTraces } from '../../story/types'
import type { GameEvent } from '../events'
import { siteHash, type StorySite } from '../sites'

const pos = z.number().positive()
const count = z.number().int().nonnegative()
const look = z.enum(FACTION_IDS as [FactionId, ...FactionId[]])

export const world12Schema = z.object({
  comment: z.string().optional(),
  columnNine: z.object({
    haulers: z.number().int().min(1),
    settlers: z.number().int().min(1),
    leaseBanded: count,
    spacingM: pos,
    gapM: pos,
    jitterM: z.number().nonnegative(),
    haulerAsset: z.string().min(1),
    settlerAsset: z.string().min(1),
    leaseLook: look,
    plainLook: look,
    stripDays: z.number().int().min(1),
    strippedLeft: count,
    memorialBeacons: count,
    memorialVisitors: count,
  }),
  scene: z.object({
    beaconAsset: z.string().min(1),
    beaconLook: look,
    beaconRingM: pos,
    visitorAsset: z.string().min(1),
    visitorLook: look,
    visitorOffsetM: pos,
    visitorSpacingM: pos,
    bargeWreckAsset: z.string().min(1),
    bargeWreckLook: look,
    bargeRigAsset: z.string().min(1),
    bargeRigLook: look,
  }),
  m1: z.object({ acceptCommands: z.array(storyCommandSchema) }),
  m2: z.object({
    watchRows: z.number().int().min(1),
    hoursPerTonne: pos,
    minHours: pos,
    maxHours: pos,
    dayPrefix: z.string(),
    place: z.string().min(1),
    fallbackRow: z.object({ day: z.string(), livery: z.string(), hull: z.string(), hours: z.string() }),
    bargeRow: z.object({ wreck: z.array(z.string()).length(4), rig: z.array(z.string()).length(4) }),
    overlayLog: z.string().min(1),
    liveries: z.record(z.string(), z.string().min(1)),
    liveryFallback: z.string().min(1),
  }),
})
export type World12Config = z.infer<typeof world12Schema>

let cached: World12Config | null = null
/** `story/world12.json`, parsed once. */
export function world12Config(): World12Config {
  cached ??= world12Schema.parse(world12Json)
  return cached
}

export type ColumnNineKind = 'hauler' | 'settler'

/** One hulk of the field. */
export interface ColumnNineHull {
  /** Stable key (`c9:<queue>`), also the hulk's POI-local id. */
  readonly key: string
  /** Place in the queue, 0 = the front. */
  readonly queue: number
  readonly kind: ColumnNineKind
  readonly assetId: string
  /** The render look: `player_lease` (Tethys yellow; the settler's cream lease band) or the plain look. */
  readonly look: FactionId
  readonly leaseBand: boolean
  /** World position, float64 metres in the site's system. */
  readonly position: Vec3T
  /** The lead hauler carries the flight recorder. */
  readonly recorder: boolean
}

export interface ColumnNineField {
  readonly systemId: string
  readonly hulls: readonly ColumnNineHull[]
  /** Unit vector from the back of the queue to its front. */
  readonly axis: Vec3T
}

function unit(h: number, k: number): number {
  return (((h >>> (k * 8)) & 0xff) / 255) * 2 - 1
}

/** The level unit vector at right angles to `axis` in the x–z plane (the queue's side; +x when the axis is vertical). */
export function levelSide(axis: Vec3T): { lx: number; lz: number } {
  const l = Math.hypot(axis[0], axis[2])
  return l > 1e-9 ? { lx: -axis[2] / l, lz: axis[0] / l } : { lx: 1, lz: 0 }
}

/** The field's hulls, front to back, from the galaxy seed (the same seed always gives the same field). */
export function columnNineField(site: StorySite, seed: number, cfg: World12Config = world12Config()): ColumnNineField {
  const c = cfg.columnNine
  const h = siteHash(seed, `${site.systemId}/column-nine/axis`)
  // A queue mostly in the disc plane (the world x–z, y up; small y), like the M3 sites. ST-1: this was the x–y plane.
  let ax = unit(h, 0)
  let az = unit(h, 1)
  const ay = unit(h, 2) * 0.1
  if (Math.abs(ax) + Math.abs(az) < 1e-3) ax = 1
  const len = Math.sqrt(ax * ax + ay * ay + az * az)
  ax /= len
  az /= len
  const axis: Vec3T = [ax, ay / len, az]
  // A level lateral direction (in the x–z plane, at right angles to the axis) for the jitter.
  const { lx, lz } = levelSide(axis)
  const total = c.haulers + c.settlers
  const span = (total - 2) * c.spacingM + c.gapM
  const hulls: ColumnNineHull[] = []
  for (let q = 0; q < total; q++) {
    const hauler = q < c.haulers
    const along = span / 2 - (hauler ? q * c.spacingM : (c.haulers - 1) * c.spacingM + c.gapM + (q - c.haulers) * c.spacingM)
    const j = unit(siteHash(seed, `${site.systemId}/column-nine/${q}`), 0) * c.jitterM
    const leaseBand = !hauler && q >= total - c.leaseBanded
    hulls.push({
      key: `c9:${q}`,
      queue: q,
      kind: hauler ? 'hauler' : 'settler',
      assetId: hauler ? c.haulerAsset : c.settlerAsset,
      look: hauler || leaseBand ? c.leaseLook : c.plainLook,
      leaseBand,
      position: [site.position[0] + axis[0] * along + lx * j, site.position[1] + axis[1] * along, site.position[2] + axis[2] * along + lz * j],
      recorder: q === 0,
    })
  }
  return { systemId: site.systemId, hulls, axis }
}

/** Securing a hulk of the field: the lead hauler gives the recorder pickup, any other hulk nothing. */
export function recorderPickup(hullKey: string): GameEvent | null {
  return hullKey === 'c9:0' ? { kind: 'storyItemPicked', itemId: 'column_nine_recorder' } : null
}

// ---------------------------------------------------------------------------------------------
// The lasting trace
// ---------------------------------------------------------------------------------------------

/** The M1 trace from the fork's flags, recorded once with today's online day (the same career before the fork, or once recorded). */
export function recordM1Trace(career: Career): Career {
  const s = storyOf(career)
  if (s.traces?.columnNine !== undefined) return career
  const kind = hasFlag(career, 'manifest_recovered') ? 'memorial' : hasFlag(career, 'manifest_sold') ? 'stripped' : null
  if (kind === null) return career
  const traces: StoryTraces = { ...s.traces, columnNine: { kind, onlineDay: s.onlineDays } }
  return withStory(career, (st) => ({ ...st, traces }))
}

export interface ColumnNineTrace {
  readonly kind: 'field' | 'memorial' | 'stripped'
  /** Hulks still in the field (queue order kept; stripping takes settlers from the back first). */
  readonly hullsLeft: number
  readonly beacons: number
  /** Holdfast visitor ships at the memorial. */
  readonly visitors: number
}

/** How the field looks today: whole before the fork, a memorial when kept, stripped down over `stripDays` when sold. */
export function columnNineTrace(career: Career, cfg: World12Config = world12Config()): ColumnNineTrace {
  const c = cfg.columnNine
  const total = c.haulers + c.settlers
  const t = storyOf(career).traces?.columnNine
  if (t === undefined) return { kind: 'field', hullsLeft: total, beacons: 0, visitors: 0 }
  if (t.kind === 'memorial') return { kind: 'memorial', hullsLeft: total, beacons: c.memorialBeacons, visitors: c.memorialVisitors }
  const days = Math.max(0, storyOf(career).onlineDays - t.onlineDay)
  const left = Math.min(total, c.strippedLeft)
  const f = Math.min(1, days / c.stripDays)
  return { kind: 'stripped', hullsLeft: Math.round(total - (total - left) * f), beacons: 0, visitors: 0 }
}

/** The hulls still standing for a trace: the front of the queue stays longest (the haulers are stripped last). */
export function standingHulls(field: ColumnNineField, trace: ColumnNineTrace): readonly ColumnNineHull[] {
  return trace.hullsLeft >= field.hulls.length ? field.hulls : field.hulls.slice(0, trace.hullsLeft)
}

// ---------------------------------------------------------------------------------------------
// The accept-time commands (the Asset Recovery crew)
// ---------------------------------------------------------------------------------------------

/**
 * Mission 1's accept-time commands (`world12.json m1.acceptCommands`: the Asset Recovery crew at the field), issued
 * once when the mission goes active: by an accept, or by doing its first step without one. The issue is recorded in
 * `career.story.traces.m1Crew`, so a later result, a reload or a second accept never issues them again (the worker
 * keys story ships as well). Nothing while the mission is offered, complete or absent.
 */
export function m1AcceptCommands(career: Career, cfg: World12Config = world12Config()): { career: Career; commands: readonly StoryCommand[] } {
  const s = storyOf(career)
  if (s.traces?.m1Crew !== undefined || missionOf(career, 'm1')?.state !== 'active') return { career, commands: [] }
  const traces: StoryTraces = { ...s.traces, m1Crew: { onlineDay: s.onlineDays } }
  return { career: withStory(career, (st) => ({ ...st, traces })), commands: cfg.m1.acceptCommands }
}
